/**
 * Helper functions to map filter numeric IDs to API string values
 */

import { AmenityApiMapping, PropertyTypeApiMapping, ProvisionApiMapping } from '@app/enums';
import { ProvisionType } from '@app/models/api/api-types';
import { MapAccommodationsFilters } from '@app/models/map-accommodations.model';

/**
 * Mapping from ProvisionApiMapping numeric IDs to ProvisionType enum values
 */
export const PROVISION_API_TO_TYPE_MAPPING: Record<number, ProvisionType> = {
    [ProvisionApiMapping.Breakfast]: ProvisionType.Breakfast,
    [ProvisionApiMapping.HalfBoard]: ProvisionType.HalfBoard,
    [ProvisionApiMapping.AllInclusive]: ProvisionType.AllInclusive,
    [ProvisionApiMapping.FullBoard]: ProvisionType.FullBoard,
    [ProvisionApiMapping.NoMeal]: ProvisionType.NoMeal,
};

export const mapFilterValues = <TInput extends string | number | symbol, TOutput>(
    inputIds: Array<TInput>,
    apiMap: Record<TInput, TOutput>,
    overrides?: Partial<Record<TInput, TOutput>>,
): Array<TOutput> => {
    return inputIds.map(id => overrides?.[id] ?? apiMap[id]).filter(Boolean);
};

/**
 * A few property types are surfaced in the UI (and by `availableOptions`) under a
 * different name than the category slug the listing-search API expects. The enum
 * and the filter list call id 277 `HomeOffice`, but the search endpoint still
 * categorises it as `Unknown` — so we translate it only here, at the API boundary.
 */
const PROPERTY_TYPE_API_CATEGORY_OVERRIDES: Partial<Record<number, string>> = {
    [PropertyTypeApiMapping.HomeOffice]: 'Unknown',
};

/**
 * Maps provision IDs from ProvisionApiMapping to ProvisionType enum values
 * @param provisionIds Array of provision IDs (numeric)
 * @returns Array of ProvisionType enum values
 */
export const mapProvisionToTypes = (
    provisionIds?: (number | undefined)[],
): ProvisionType[] | undefined => {
    if (!provisionIds) return undefined;

    const filtered = provisionIds.filter((id): id is number => id !== undefined);
    return filtered.length > 0
        ? mapFilterValues(filtered, PROVISION_API_TO_TYPE_MAPPING)
        : undefined;
};

export const convertFiltersToApiParams = (filters: MapAccommodationsFilters) => {
    const { propertyTypeIds = [], amenityIds = [], provisionIds = [] } = filters;

    return {
        propertyType: mapFilterValues<number, string>(
            propertyTypeIds,
            PropertyTypeApiMapping,
            PROPERTY_TYPE_API_CATEGORY_OVERRIDES,
        ),
        amenities: mapFilterValues<number, string>(amenityIds, AmenityApiMapping),
        provision: mapFilterValues<number, string>(provisionIds, ProvisionApiMapping),
    };
};
